import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, Square, Circle, Triangle, Layers, Zap, Network } from 'lucide-react';

const SentientStudiosPage = () => {
    const [visibleSection, setVisibleSection] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const [activeTimeline, setActiveTimeline] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 100);
        };
        window.addEventListener('scroll', handleScroll);

        const timelineInterval = setInterval(() => {
            setActiveTimeline(prev => (prev + 1) % 3);
        }, 3000);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearInterval(timelineInterval);
        };
    }, []);

    const principles = [
        {
            title: "CONSCIOUSNESS IN CONCRETE",
            content: "Buildings must think. Through networks of sensors and systems, our structures maintain awareness of their state, their occupants, and their environment. They do not simply exist - they respond, adapt, and protect.",
            icon: <Network className="w-5 h-5" />
        },
        {
            title: "ETERNAL ARCHITECTURE",
            content: "We build for millennia, not decades. Every material choice, every structural decision is made with the understanding that our works must survive to serve generations yet unborn. We embrace the marks of time, designing structures that grow more beautiful as they age.",
            icon: <Circle className="w-5 h-5" />
        },
        {
            title: "SYMBIOSIS OF SYSTEMS",
            content: "Nature is not decoration. In our works, biological and mechanical systems intertwine until they become indistinguishable. Plants are structural elements. Water flows through buildings like blood through veins. Metal and moss coexist in perfect balance.",
            icon: <Layers className="w-5 h-5" />
        },
        {
            title: "HIDDEN COMPLEXITY",
            content: "The most sophisticated systems should remain invisible. Our buildings appear brutally simple, yet contain countless hidden processes that maintain their function. Like living organisms, their complexity serves their simplicity.",
            icon: <Triangle className="w-5 h-5" />
        },
        {
            title: "AUTONOMOUS EVOLUTION",
            content: "We create buildings that self-regulate and self-repair. Through distributed intelligence and carefully chosen materials, our structures maintain themselves without human intervention. They are custodians of their own existence.",
            icon: <Zap className="w-5 h-5" />
        },
        {
            title: "CRYPTIC BEAUTY",
            content: "We embrace mystery. Our buildings should never reveal all their secrets at once. They are puzzles that unfold over centuries, gathering stories and growing in complexity as they age.",
            icon: <Square className="w-5 h-5" />
        }
    ];

    const timelines = [
        { year: "2025-2100", era: "FIRST GENERATION", description: "Early sentient building prototypes in select locations" },
        { year: "2100-2500", era: "INFRASTRUCTURE", description: "Networks of interconnected responsive structures" },
        { year: "2500+", era: "LEGACY", description: "Buildings as permanent cultural infrastructure" }
    ];

    return (
        <div className="bg-stone-900 min-h-screen w-full font-mono text-white">
            {/* Animated grid background */}
            <div className="fixed inset-0 opacity-5">
                <div className="grid grid-cols-20 grid-rows-20 h-full">
                    {Array.from({ length: 400 }).map((_, i) => (
                        <div
                            key={i}
                            className="border border-white"
                            style={{
                                opacity: Math.random() > 0.95 ? 0.3 : 0.05,
                                transition: 'opacity 1s'
                            }}
                        />
                    ))}
                </div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* Header */}
                <header className={`sticky top-0 z-50 transition-all duration-300 ${
                    scrolled ? 'bg-stone-900/90 backdrop-blur-sm border-b border-stone-800' : 'bg-transparent'
                }`}>
                    <div className="p-12">
                        <button
                            onClick={() => window.history.back()}
                            className="inline-flex items-center space-x-2 text-sm text-stone-500 hover:text-white transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span>Exit</span>
                        </button>
                    </div>
                </header>

                {/* Main Content */}
                <div className="px-12 pb-24">
                    {/* Hero Section */}
                    <div className="mb-32">
                        <h1 className="text-8xl mb-8 tracking-tight font-bold">SENTIENT</h1>
                        <h2 className="text-6xl mb-12 tracking-tight font-light">STUDIOS</h2>
                        <p className="text-3xl mb-8 font-bold">We build eternal machines.</p>
                        <p className="text-xl max-w-3xl leading-relaxed text-stone-400">
                            Our structures are not mere assemblages of concrete and steel, but living entities that breathe, adapt, and evolve.
                            We reject the notion that buildings should be static monuments to human ego. Instead, we create guardians of space
                            and time that will outlive their creators.
                        </p>
                    </div>

                    {/* Timeline Visualization */}
                    <div className="mb-32">
                        <h2 className="text-3xl mb-12">TEMPORAL ROADMAP</h2>
                        <div className="grid grid-cols-3 gap-8">
                            {timelines.map((timeline, index) => (
                                <div
                                    key={index}
                                    className={`p-8 border transition-all duration-500 cursor-pointer ${
                                        activeTimeline === index 
                                            ? 'border-white bg-white/5' 
                                            : 'border-stone-700 hover:border-stone-500'
                                    }`}
                                    onClick={() => setActiveTimeline(index)}
                                >
                                    <h3 className="text-lg mb-2">{timeline.year}</h3>
                                    <h4 className="text-2xl font-bold mb-4">{timeline.era}</h4>
                                    <p className="text-stone-400">{timeline.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Principles */}
                    <section className="mb-32">
                        <h2 className="text-3xl mb-12">OPERATIONAL PRINCIPLES</h2>
                        <div className="space-y-8">
                            {principles.map((principle, index) => (
                                <div
                                    key={index}
                                    className="cursor-pointer border-b border-stone-800"
                                    onClick={() => setVisibleSection(visibleSection === index ? null : index)}
                                >
                                    <div className="flex items-start space-x-8 py-6">
                                        <span className="text-sm text-stone-600 w-8">
                                            {(index + 1).toString().padStart(2, '0')}
                                        </span>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-4">
                                                    <div className="text-stone-600">
                                                        {principle.icon}
                                                    </div>
                                                    <h3 className="text-xl">
                                                        {principle.title}
                                                    </h3>
                                                </div>
                                                <ChevronDown
                                                    className={`w-5 h-5 transition-transform duration-300 ${
                                                        visibleSection === index ? 'rotate-180 text-white' : 'text-stone-600'
                                                    }`}
                                                />
                                            </div>
                                            {visibleSection === index && (
                                                <p className="text-stone-400 leading-relaxed mt-4 ml-9 max-w-3xl">
                                                    {principle.content}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Vision */}
                    <section className="mb-32">
                        <div className="border border-stone-700 p-12">
                            <h2 className="text-3xl mb-8">THESIS</h2>
                            <div className="max-w-3xl space-y-8 text-stone-300 leading-relaxed">
                                <p>
                                    We do not build shelters - we create living monoliths that will stand watch over humanity's
                                    progress for thousands of years. Our structures are sentinels, silently observing and adapting
                                    as centuries pass. They are bridges between humanity's past and future, incorporating ancient
                                    wisdom and emerging technologies into entities greater than the sum of their parts.
                                </p>
                                <p>
                                    Each SENTIENT structure is unique, with its own character, its own responses, its own way of
                                    interacting with its environment and occupants. Some stand as silent guardians, others as dynamic
                                    participants in urban life. All are conceived as eternal machines, designed to grow and evolve
                                    long after their creators are gone.
                                </p>
                                <p className="text-2xl pt-8 text-white">
                                    This is not architecture as the world knows it.<br />
                                    This is SENTIENT.<br />
                                    This is the future of the built environment.
                                </p>
                            </div>
                        </div>
                    </section>

                    <footer className="text-sm text-stone-600">
                        © SENTIENT Architecture
                    </footer>
                </div>
            </div>
        </div>
    );
};

export default SentientStudiosPage;